import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { clsx } from 'clsx';
import { api } from '../services/api';
import { PipelineOrder, PipelineShortage } from '../types';
import { Card, LoadingPage } from '../components/Layout';

export function ComingUpPage() {
  const [orders, setOrders] = useState<PipelineOrder[]>([]);
  const [shortages, setShortages] = useState<Map<string, PipelineShortage>>(new Map());
  const [loading, setLoading] = useState(true);
  const [checkingShortages, setCheckingShortages] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);

  useEffect(() => {
    loadPipelineOrders();
  }, []);

  const loadPipelineOrders = async () => {
    try {
      const data = await api.getPipelineOrders();
      setOrders(data);

      // Auto-check shortages for all pipeline orders
      if (data.length > 0) {
        setCheckingShortages(true);
        try {
          const orderIds = data.map(o => o.id);
          const results = await api.checkPipelineShortages(orderIds);
          const map = new Map<string, PipelineShortage>();
          for (const r of results) {
            map.set(r.orderId, r);
          }
          setShortages(map);
        } catch (error) {
          console.error('Failed to check shortages:', error);
        } finally {
          setCheckingShortages(false);
        }
      }
    } catch (error) {
      console.error('Failed to load pipeline orders:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingPage />;
  }

  const rushCount = orders.filter(o => o.isRush).length;
  const shortageCount = Array.from(shortages.values()).filter(s => s.hasShortages).length;

  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="text-center">
          <div className="text-2xl font-bold text-white">{orders.length}</div>
          <div className="text-xs text-gray-400">Pipeline</div>
        </Card>
        <Card className="text-center">
          <div className="text-2xl font-bold text-yellow-400">{rushCount}</div>
          <div className="text-xs text-gray-400">Rush</div>
        </Card>
        <Card className="text-center">
          <div className={clsx('text-2xl font-bold', shortageCount > 0 ? 'text-red-400' : 'text-green-400')}>
            {checkingShortages ? '...' : shortageCount}
          </div>
          <div className="text-xs text-gray-400">Shortages</div>
        </Card>
      </div>

      {/* Pipeline Orders */}
      {orders.map((order) => (
        <PipelineCard
          key={order.id}
          order={order}
          shortage={shortages.get(order.id)}
          checkingShortages={checkingShortages}
          expanded={expandedOrder === order.id}
          onToggleExpand={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
        />
      ))}

      {orders.length === 0 && (
        <Card className="text-center py-12">
          <div className="text-gray-400 text-lg">No orders in pipeline</div>
          <div className="text-gray-500 text-sm mt-2">
            Orders with deposit paid, paid in full, or credit approved will appear here
          </div>
        </Card>
      )}
    </div>
  );
}

interface PipelineCardProps {
  order: PipelineOrder;
  shortage?: PipelineShortage;
  checkingShortages: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
}

function PipelineCard({ order, shortage, checkingShortages, expanded, onToggleExpand }: PipelineCardProps) {
  const getPaymentBadge = () => {
    if (order.paidInFull) return { label: 'PAID', color: 'bg-green-500/20 text-green-400' };
    if (order.depositPaid) return { label: 'DEPOSIT', color: 'bg-blue-500/20 text-blue-400' };
    if (order.creditApproved) return { label: 'CREDIT', color: 'bg-yellow-500/20 text-yellow-400' };
    return { label: 'PENDING', color: 'bg-gray-500/20 text-gray-400' };
  };

  const paymentBadge = getPaymentBadge();

  const getShortageIndicator = () => {
    if (checkingShortages) {
      return <span className="text-gray-500 text-xs animate-pulse">Checking...</span>;
    }
    if (!shortage) {
      return <span className="text-gray-500 text-xs">--</span>;
    }
    if (shortage.hasShortages) {
      return (
        <button
          onClick={onToggleExpand}
          className="flex items-center gap-1 text-red-400 text-sm font-medium"
        >
          <span className="text-red-500">&#9888;</span> {shortage.shortageCount} shortage{shortage.shortageCount !== 1 ? 's' : ''}
        </button>
      );
    }
    return <span className="text-green-400 text-sm">&#10003; Stock OK</span>;
  };

  const borderColor = shortage?.hasShortages
    ? 'border-l-red-500'
    : order.isRush
    ? 'border-l-yellow-400'
    : 'border-l-gray-600';

  return (
    <Card className={clsx('border-l-4', borderColor)}>
      {/* Header Row */}
      <div className="flex justify-between items-start mb-2">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-white font-medium">{order.orderNumber}</span>
            {order.isRush && (
              <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded font-bold">RUSH</span>
            )}
            <span className={clsx('px-2 py-0.5 text-xs rounded', paymentBadge.color)}>
              {paymentBadge.label}
            </span>
          </div>
          <div className="text-sm text-gray-400 mt-0.5">{order.companyName}</div>
          {order.sidemark && (
            <div className="text-xs text-gray-500">{order.sidemark}</div>
          )}
        </div>
        <div className="text-right">
          {getShortageIndicator()}
        </div>
      </div>

      {/* Details Row */}
      <div className="grid grid-cols-3 gap-2 text-sm mt-3">
        <div>
          <span className="text-gray-500">Items:</span>
          <span className="text-gray-300 ml-1">{order.itemCount}</span>
        </div>
        <div>
          <span className="text-gray-500">Status:</span>
          <span className="text-gray-300 ml-1 capitalize">{order.status.replace(/_/g, ' ')}</span>
        </div>
        <div>
          {order.neededByDate ? (
            <>
              <span className="text-gray-500">Need by:</span>
              <span className={clsx(
                'ml-1',
                new Date(order.neededByDate) < new Date() ? 'text-red-400' : 'text-gray-300'
              )}>
                {format(new Date(order.neededByDate), 'MMM d')}
              </span>
            </>
          ) : (
            <>
              <span className="text-gray-500">Ordered:</span>
              <span className="text-gray-300 ml-1">{format(new Date(order.createdAt), 'MMM d')}</span>
            </>
          )}
        </div>
      </div>

      {/* Total */}
      {order.totalAmount > 0 && (
        <div className="text-sm text-gray-500 mt-2">
          ${order.totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
      )}

      {/* Expanded Shortage Details */}
      {expanded && shortage && shortage.hasShortages && (
        <div className="mt-3 pt-3 border-t border-gray-700">
          <div className="text-sm font-medium text-red-400 mb-2">Inventory Shortages</div>
          <div className="space-y-2">
            {shortage.shortages.map((s, idx) => (
              <div key={idx} className="bg-red-500/5 border border-red-500/20 rounded p-2">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-sm text-white font-mono">{s.partNumber}</div>
                    <div className="text-xs text-gray-400">{s.description}</div>
                  </div>
                  <div className="text-right text-xs">
                    <div className="text-red-400">Short: {s.shortage.toFixed(1)} {s.unit}</div>
                    <div className="text-gray-500">Need {s.required.toFixed(1)} / Have {s.available.toFixed(1)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
